/**
 * StorefrontChat — merchant dashboard inbox + thread (read-only, Phase 4).
 *
 * Two-pane layout (list + detail) on desktop, stacked on mobile via URL state.
 * URL drives selection: /storefront-chat shows the list only; selecting a
 * conversation routes to /storefront-chat/<id>.
 *
 * Polling: inbox list every 8s, current thread every 4s. Replaced by socket
 * in Phase 5. Take-over / reply / snooze / priority controls land in Phase 7.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { io as socketIo } from 'socket.io-client';
import {
  MessageSquare, Search, ChevronLeft, AlertCircle, Globe2, Smartphone, Monitor,
  UserCheck, UserMinus, Clock, Flag, MoreHorizontal, XCircle, Download,
} from 'lucide-react';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import axiosInstance from '../../axiosConfig';
import MerchantComposer from './MerchantComposer';
import './StorefrontChat.css';

// Read current user id from the JWT payload so we can render "Yours" when
// `takenOverBy` matches the merchant viewing the dashboard.
function getCurrentUserId() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.userId || payload?.id || null;
  } catch {
    return null;
  }
}

// Match the URL strategy used by frontend/src/features/whatsapp/hooks/useSocket.js
function getSocketUrl() {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:4000';
  }
  return 'https://revstar-main-platform-production.up.railway.app';
}

function getMerchantToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
}

// Single source of truth for "are we on a phone-sized viewport". Used to fold
// the conversation header's action toolbar (Take over / Snooze / Priority /
// Close) into one kebab menu so the header stays a single row.
function useIsMobile(maxWidth = 640) {
  const [is, setIs] = useState(() => (
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches
      : false
  ));
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (e) => setIs(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [maxWidth]);
  return is;
}

const TABS = [
  { key: 'active',   label: 'Active' },
  { key: 'pending',  label: 'Pending' },
  { key: 'closed',   label: 'Closed' },
  { key: 'archived', label: 'Archived' },
];

const INBOX_POLL_MS = 8000;
const THREAD_POLL_MS = 4000;

// ---------- Helpers --------------------------------------------------------

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function dayKey(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function timeOfDay(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function visitorLabel(visitor) {
  if (visitor?.deletedAt) return 'Deleted user';
  return visitor?.displayName || visitor?.name || 'Visitor';
}

function initials(visitor) {
  if (visitor?.deletedAt) return '–';
  const source = visitor?.name || visitor?.displayName || 'G';
  // "Guest #4821" → "G"; "Tola O" → "TO"; "tola@x.com" → "T"
  const parts = source.replace(/^Guest\s*#?/i, 'G').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => (p[0] || '').toUpperCase()).join('') || 'G';
}

function deviceIcon(device) {
  if (device === 'mobile') return Smartphone;
  if (device === 'tablet') return Smartphone;
  return Monitor;
}

// Tag chip editor for the right rail. Reads effective tags from the visitor
// (linked customer's tags take precedence over pre-link visitor.tags), and
// writes through the merchant-chatbot tag endpoints. Optimistic-friendly:
// fires `onChanged` after each mutation so the parent re-loads the conv.
const TagEditor = ({ conversationId, visitor, onChanged }) => {
  const initial = (visitor?.customer?.tags?.length ? visitor.customer.tags : visitor?.tags) || [];
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e?.preventDefault?.();
    const tag = draft.trim();
    if (!tag || busy) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/merchant/chatbot/conversations/${conversationId}/tags`, { tag });
      setDraft('');
      setAdding(false);
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('[TagEditor] add failed:', err?.response?.data?.error || err?.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (tag) => {
    if (busy) return;
    setBusy(true);
    try {
      await axiosInstance.delete(
        `/merchant/chatbot/conversations/${conversationId}/tags/${encodeURIComponent(tag)}`,
      );
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('[TagEditor] remove failed:', err?.response?.data?.error || err?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sc-tags">
      {initial.length === 0 && !adding && (
        <span className="sc-rail__empty" style={{ fontSize: 12 }}>No tags yet.</span>
      )}
      {initial.map((t) => (
        <span key={t} className="sc-tag">
          {t}
          <button
            type="button"
            className="sc-tag__x"
            onClick={() => remove(t)}
            disabled={busy}
            aria-label={`Remove tag ${t}`}
          >×</button>
        </span>
      ))}
      {adding ? (
        <form onSubmit={add} className="sc-tag-form">
          <input
            autoFocus
            className="sc-tag-form__input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { if (!draft) setAdding(false); }}
            placeholder="Tag…"
            maxLength={40}
          />
        </form>
      ) : (
        <button type="button" className="sc-tag-add" onClick={() => setAdding(true)}>+ Add</button>
      )}
    </div>
  );
};

// Inline preview of any message attachment, mirrored on visitor + dashboard.
// Keeps the bubble visually distinct from text-only messages.
const AttachmentPreview = ({ attachment: a, role }) => {
  if (!a || !a.type) return null;
  const sideClass = `sc-bubble sc-bubble--${role || 'merchant'}`;
  if (a.type === 'product_card') {
    return (
      <div className={sideClass} style={{ padding: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
        {a.image && <img src={a.image} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
          {a.price !== undefined && (
            <div style={{ fontSize: 11.5, opacity: 0.8, fontVariantNumeric: 'tabular-nums' }}>
              {new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(parseFloat(a.price) || 0)}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (a.type === 'image' && a.url) {
    return (
      <a href={a.url} target="_blank" rel="noopener noreferrer" className={sideClass} style={{ padding: 4, display: 'inline-block' }}>
        <img src={a.url} alt={a.name || ''} style={{ maxWidth: 220, maxHeight: 220, borderRadius: 6, display: 'block' }} />
      </a>
    );
  }
  if (a.type === 'discount_code') {
    return (
      <div className={sideClass} style={{ padding: '6px 10px', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.85 }}>Coupon</span>
        <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.18)' }}>{a.code}</code>
        {a.label && <span style={{ fontSize: 11.5, opacity: 0.9 }}>{a.label}</span>}
      </div>
    );
  }
  if (a.type === 'button_link' && a.url) {
    return (
      <a
        href={a.url}
        target="_blank"
        rel="noopener noreferrer"
        className={sideClass}
        style={{ padding: '8px 14px', textDecoration: 'none', fontSize: 12.5, fontWeight: 600, color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        {a.label || 'Open link'}
        <span aria-hidden>↗</span>
      </a>
    );
  }
  return null;
};

// ---------- Inbox list -----------------------------------------------------

const InboxList = ({ activeTab, setActiveTab, query, setQuery, items, total, loading, selectedId, onSelect }) => (
  <aside className="sc-list">
    <div className="sc-list__header">
      <div className="sc-list__title-row">
        <h2 className="sc-list__title">Storefront chat</h2>
        <Link
          to="/storefront-chat/live"
          title="View live storefront activity"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 9px',
            background: 'rgba(13, 116, 99, 0.08)',
            color: '#0d7463',
            border: '1px solid rgba(13, 116, 99, 0.22)',
            borderRadius: 999,
            textDecoration: 'none',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: 999,
              background: '#22c55e',
              display: 'inline-block',
              animation: 'sc-live-pulse 1.4s ease-in-out infinite',
            }}
            aria-hidden
          />
          Live
        </Link>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {total} total
      </p>
      <style>{`@keyframes sc-live-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(1.4); } }`}</style>
      <div className="sc-search">
        <Search size={14} className="sc-search__icon" aria-hidden />
        <input
          type="search"
          className="sc-search__input"
          placeholder="Search messages, names, emails…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="sc-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeTab === t.key}
            className={`sc-tab ${activeTab === t.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>

    <div className="sc-list__items">
      {loading && items.length === 0 && (
        <div className="sc-loading"><span className="sc-spinner" aria-hidden />Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <p className="sc-empty">No conversations in this tab yet.</p>
      )}
      {items.map((c) => (
        <InboxRow
          key={c.id}
          conversation={c}
          isActive={c.id === selectedId}
          onSelect={() => onSelect(c.id)}
        />
      ))}
    </div>
  </aside>
);

const InboxRow = ({ conversation: c, isActive, onSelect }) => {
  const visitor = c.visitor || {};
  const isUnread = (c.unreadByMerchant || 0) > 0;
  const isMine = !!c.takenOverBy;
  return (
    <Link
      to={`/storefront-chat/${c.id}`}
      className={`sc-row ${isActive ? 'is-active' : ''} ${isUnread ? 'is-unread' : ''}`}
      onClick={(e) => { e.preventDefault(); onSelect(); }}
    >
      <span className="sc-row__avatar" aria-hidden>{initials(visitor)}</span>
      <div className="sc-row__body">
        <div className="sc-row__name-row">
          <p className="sc-row__name">{visitorLabel(visitor)}</p>
          {!visitor.deletedAt && visitor.country && <span className="sc-row__country">{visitor.country}</span>}
        </div>
        <p className="sc-row__preview">
          {c.lastMessageDirection === 'outbound' ? 'You: ' : ''}
          {c.lastMessageText || '—'}
        </p>
        <div className="sc-row__chips" style={{ marginTop: '4px' }}>
          {c.status === 'AI_HANDLING' && <span className="sc-chip sc-chip--ai">AI</span>}
          {c.status === 'SNOOZED' && <span className="sc-chip sc-chip--snoozed">Snoozed</span>}
          {c.status === 'CLOSED' && <span className="sc-chip sc-chip--closed">Closed</span>}
          {isMine && c.status === 'MERCHANT_HANDLING' && <span className="sc-chip sc-chip--you">Yours</span>}
          {c.intent === 'sales' && <span className="sc-chip sc-chip--sales">Sales</span>}
          {c.intent === 'support' && <span className="sc-chip sc-chip--support">Support</span>}
          {c.priority === 'URGENT' && <span className="sc-chip sc-chip--urgent">Urgent</span>}
          {c.priority === 'HIGH' && <span className="sc-chip sc-chip--high">High</span>}
          {(visitor.effectiveTags || []).slice(0, 2).map((t) => (
            <span key={t} className="sc-chip sc-chip--tag">{t}</span>
          ))}
          {c.attribution?.orders > 0 && (
            <span className="sc-chip sc-chip--revenue" title={`${c.attribution.orders} order(s) attributed`}>
              Sold
            </span>
          )}
        </div>
      </div>
      <div className="sc-row__meta">
        <span className="sc-row__time">{relativeTime(c.lastMessageAt)}</span>
        {isUnread && <span className="sc-row__unread">{c.unreadByMerchant}</span>}
      </div>
    </Link>
  );
};

// ---------- Conversation thread + visitor rail -----------------------------

// Conversation header action toolbar — take over / release / snooze / priority
// / close. Each action posts to the backend then triggers `onChanged` which
// re-fetches the conversation so the new state (status / priority / etc.)
// reflects immediately even if the socket event hasn't arrived yet.
const ConversationActions = ({ data, acting, setActing, menuOpen, setMenuOpen, onChanged }) => {
  const status = data.status;
  const isClosed = status === 'CLOSED';
  const isMerchantHandling = status === 'MERCHANT_HANDLING';
  const isMobile = useIsMobile(640);

  const post = async (path, body) => {
    if (acting) return;
    setActing(true);
    try {
      await axiosInstance.post(`/merchant/chatbot/conversations/${data.id}${path}`, body || {});
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('[ConversationActions]', path, err?.response?.data?.error || err?.message);
    } finally {
      setActing(false);
    }
  };

  const setPriority = async (priority) => {
    if (acting) return;
    setActing(true);
    try {
      await axiosInstance.put(`/merchant/chatbot/conversations/${data.id}/priority`, { priority });
      if (onChanged) await onChanged();
    } catch (err) {
      console.error('[ConversationActions] priority', err?.response?.data?.error || err?.message);
    } finally {
      setActing(false);
      setMenuOpen(null);
    }
  };

  const snooze = async (hours) => {
    await post('/snooze', { hours });
    setMenuOpen(null);
  };

  const closeConv = async () => {
    if (typeof window !== 'undefined' && !window.confirm('Close this conversation?')) return;
    await post('/close', { reason: 'merchant_closed' });
    setMenuOpen(null);
  };

  const downloadTranscript = async () => {
    try {
      const res = await axiosInstance.get(
        `/merchant/chatbot/conversations/${data.id}/transcript.pdf`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-${data.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[ConversationActions] transcript download failed:', err?.message);
    } finally {
      setMenuOpen(null);
    }
  };

  const PRIORITIES = [
    { key: 'LOW',    label: 'Low' },
    { key: 'MEDIUM', label: 'Medium' },
    { key: 'HIGH',   label: 'High' },
    { key: 'URGENT', label: 'Urgent' },
  ];
  const SNOOZE_OPTS = [
    { key: 1,  label: '1 hour' },
    { key: 4,  label: '4 hours' },
    { key: 24, label: '24 hours' },
    { key: 0,  label: 'Until visitor messages' },
  ];

  // On mobile we collapse the toolbar into a single kebab. The dropdown
  // carries every action (Take over / Release, Snooze submenu, Priority
  // submenu, Close) so the header stays one row tall on phones.
  if (isMobile) {
    return (
      <div className="sc-actions sc-actions--mobile">
        <div className="sc-menu-wrap">
          <button
            type="button"
            className="sc-action-btn sc-action-btn--icon"
            onClick={() => setMenuOpen(menuOpen === 'more' ? null : 'more')}
            disabled={acting}
            aria-label="Conversation actions"
            title="Actions"
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen === 'more' && (
            <div className="sc-menu sc-menu--rich" role="menu">
              {!isClosed && (
                <button
                  type="button"
                  className={`sc-menu__item ${isMerchantHandling ? '' : 'sc-menu__item--primary'}`}
                  onClick={() => { post(isMerchantHandling ? '/release' : '/take-over'); setMenuOpen(null); }}
                  disabled={acting}
                >
                  {isMerchantHandling
                    ? (<><UserMinus size={14} style={{ marginRight: 8 }} /> Release to AI</>)
                    : (<><UserCheck size={14} style={{ marginRight: 8 }} /> Take over</>)}
                </button>
              )}

              {!isClosed && (
                <>
                  <div className="sc-menu__group-label">Snooze</div>
                  {SNOOZE_OPTS.map((opt) => (
                    <button
                      key={`s-${opt.key}`}
                      type="button"
                      className="sc-menu__item sc-menu__item--sub"
                      onClick={() => snooze(opt.key)}
                      disabled={acting}
                    >
                      {opt.label}
                    </button>
                  ))}

                  <div className="sc-menu__group-label">Priority</div>
                  {PRIORITIES.map((p) => (
                    <button
                      key={`p-${p.key}`}
                      type="button"
                      className={`sc-menu__item sc-menu__item--sub ${data.priority === p.key ? 'is-active' : ''}`}
                      onClick={() => setPriority(p.key)}
                      disabled={acting}
                    >
                      <span>{p.label}</span>
                      {data.priority === p.key && <span className="sc-menu__item-meta">Current</span>}
                    </button>
                  ))}
                </>
              )}

              <button
                type="button"
                className="sc-menu__item"
                onClick={downloadTranscript}
                disabled={acting}
              >
                <Download size={14} style={{ marginRight: 8, color: 'var(--sc-brand)' }} />
                Download transcript (PDF)
              </button>

              {!isClosed && (
                <button
                  type="button"
                  className="sc-menu__item sc-menu__item--danger"
                  onClick={closeConv}
                  disabled={acting}
                >
                  <XCircle size={14} style={{ marginRight: 8 }} />
                  Close conversation
                </button>
              )}
              {isClosed && (
                <button type="button" className="sc-menu__item" disabled>
                  This conversation is closed
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="sc-actions">
      {!isClosed && (
        isMerchantHandling ? (
          <button
            type="button"
            className="sc-action-btn"
            onClick={() => post('/release')}
            disabled={acting}
          >
            <UserMinus size={13} /> Release to AI
          </button>
        ) : (
          <button
            type="button"
            className="sc-action-btn sc-action-btn--primary"
            onClick={() => post('/take-over')}
            disabled={acting}
          >
            <UserCheck size={13} /> Take over
          </button>
        )
      )}

      <div className="sc-menu-wrap">
        <button
          type="button"
          className="sc-action-btn sc-action-btn--icon"
          onClick={() => setMenuOpen(menuOpen === 'snooze' ? null : 'snooze')}
          disabled={isClosed || acting}
          aria-label="Snooze"
          title="Snooze"
        >
          <Clock size={13} />
        </button>
        {menuOpen === 'snooze' && (
          <div className="sc-menu" role="menu">
            {SNOOZE_OPTS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className="sc-menu__item"
                onClick={() => snooze(opt.key)}
                disabled={acting}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sc-menu-wrap">
        <button
          type="button"
          className="sc-action-btn sc-action-btn--icon"
          onClick={() => setMenuOpen(menuOpen === 'priority' ? null : 'priority')}
          disabled={isClosed || acting}
          aria-label="Priority"
          title="Priority"
        >
          <Flag size={13} />
        </button>
        {menuOpen === 'priority' && (
          <div className="sc-menu" role="menu">
            {PRIORITIES.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`sc-menu__item ${data.priority === p.key ? 'is-active' : ''}`}
                onClick={() => setPriority(p.key)}
                disabled={acting}
              >
                <span>{p.label}</span>
                {data.priority === p.key && <span className="sc-menu__item-meta">Current</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="sc-menu-wrap">
        <button
          type="button"
          className="sc-action-btn sc-action-btn--icon"
          onClick={() => setMenuOpen(menuOpen === 'more' ? null : 'more')}
          disabled={acting}
          aria-label="More"
          title="More"
        >
          <MoreHorizontal size={13} />
        </button>
        {menuOpen === 'more' && (
          <div className="sc-menu" role="menu">
            <button
              type="button"
              className="sc-menu__item"
              onClick={downloadTranscript}
              disabled={acting}
            >
              <Download size={13} style={{ marginRight: 8, color: 'var(--sc-brand)' }} />
              Download transcript (PDF)
            </button>
            {!isClosed && (
              <button
                type="button"
                className="sc-menu__item"
                onClick={closeConv}
                disabled={acting}
              >
                <XCircle size={13} style={{ marginRight: 8, color: '#b91c1c' }} />
                Close conversation
              </button>
            )}
            {isClosed && (
              <button
                type="button"
                className="sc-menu__item"
                disabled
              >
                This conversation is closed
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationView = ({ conversationId, onBack, socketTick = 0, socketConnected = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null); // 'snooze' | 'priority' | 'more' | null
  const threadRef = useRef(null);
  const markedReadRef = useRef(false);
  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const load = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await axiosInstance.get(`/merchant/chatbot/conversations/${conversationId}`);
      const conv = res?.data?.conversation;
      if (!conv) throw new Error('Not found');
      setData(conv);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('[StorefrontChat] load conversation:', err?.response?.data?.error || err?.message);
      setError(err?.response?.data?.error || 'Failed to load conversation');
      setLoading(false);
    }
  }, [conversationId]);

  // Initial load + reset when conversation changes
  useEffect(() => {
    setLoading(true);
    setData(null);
    markedReadRef.current = false;
    load();
  }, [conversationId, load]);

  // Poll for new messages — backs off when socket carries the load.
  useEffect(() => {
    if (!conversationId) return undefined;
    const interval = socketConnected ? 30000 : THREAD_POLL_MS;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [conversationId, load, socketConnected]);

  // Refresh thread whenever the parent's socketTick changes
  useEffect(() => {
    if (!conversationId || socketTick === 0) return;
    load();
  }, [socketTick, conversationId, load]);

  // Mark as read once after first successful load
  useEffect(() => {
    if (!data || markedReadRef.current) return;
    markedReadRef.current = true;
    axiosInstance.post(`/merchant/chatbot/conversations/${conversationId}/mark-read`).catch(() => {});
  }, [data, conversationId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = threadRef.current;
    if (!el || !data) return;
    el.scrollTop = el.scrollHeight;
  }, [data?.messages?.length]);

  if (loading) {
    return (
      <div className="sc-detail">
        <div className="sc-loading"><span className="sc-spinner" aria-hidden />Loading conversation…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="sc-detail">
        <div className="sc-detail-empty">
          <span className="sc-detail-empty__icon" aria-hidden><AlertCircle size={24} /></span>
          <h3 className="sc-detail-empty__title">Couldn't load this conversation</h3>
          <p className="sc-detail-empty__body">{error}</p>
        </div>
      </div>
    );
  }

  const messages = Array.isArray(data?.messages) ? data.messages : [];
  const visitor = data.visitor || {};
  const DeviceIcon = deviceIcon(visitor.device);

  // Group messages by day for date dividers
  const grouped = [];
  let lastKey = null;
  messages.forEach((m) => {
    const k = dayKey(m.createdAt);
    if (k !== lastKey) {
      grouped.push({ type: 'divider', key: `d-${k}-${m.id}`, label: k });
      lastKey = k;
    }
    grouped.push({ type: 'msg', message: m });
  });

  return (
    <div className="sc-detail">
      <div className="sc-detail-grid">
        <section className="sc-conv">
          <header className="sc-conv__header">
            <button type="button" className="sc-back" onClick={onBack} aria-label="Back to list">
              <ChevronLeft size={18} />
            </button>
            <span className="sc-conv__avatar" aria-hidden>{initials(visitor)}</span>
            <div className="sc-conv__meta">
              <p className="sc-conv__title">
                {visitorLabel(visitor)}
                {data.status === 'MERCHANT_HANDLING' && data.takenOverBy === currentUserId && (
                  <span className="sc-yours-badge">Yours</span>
                )}
              </p>
              <p className="sc-conv__sub">
                {data.status === 'AI_HANDLING' && 'AI handling'}
                {data.status === 'MERCHANT_HANDLING' && (data.takenOverBy === currentUserId ? 'You are chatting' : 'Team handling')}
                {data.status === 'SNOOZED' && 'Snoozed'}
                {data.status === 'CLOSED' && 'Closed'}
                {visitor.country && <><span className="sc-conv__sub-dot" />{visitor.country}</>}
              </p>
            </div>

            <ConversationActions
              data={data}
              acting={acting}
              setActing={setActing}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              onChanged={load}
            />
          </header>

          <div className="sc-conv__thread" ref={threadRef}>
            {grouped.length === 0 && (
              <p className="sc-empty">No messages yet.</p>
            )}
            {grouped.map((item) => {
              if (item.type === 'divider') {
                return <div key={item.key} className="sc-divider">{item.label}</div>;
              }
              const m = item.message;
              const role = String(m.role || '').toLowerCase();
              const attachments = Array.isArray(m.attachments) ? m.attachments : [];
              return (
                <React.Fragment key={m.id}>
                  {m.content?.trim() && (
                    <div className={`sc-bubble sc-bubble--${role}`}>
                      {(role === 'ai' || role === 'merchant') && (
                        <span className="sc-bubble__role">{role === 'ai' ? 'AI' : 'You'}</span>
                      )}
                      {m.content}
                      <span className="sc-bubble__meta">{timeOfDay(m.createdAt)}</span>
                    </div>
                  )}
                  {attachments.map((a, i) => (
                    <AttachmentPreview key={`${m.id}-att-${i}`} attachment={a} role={role} />
                  ))}
                </React.Fragment>
              );
            })}
          </div>

          <MerchantComposer
            conversationId={conversationId}
            conversationStatus={data.status}
            onSent={load}
          />
        </section>

        <aside className="sc-rail">
          <div className="sc-rail__section">
            <span className="sc-rail__label">Visitor</span>
            <h3 className="sc-rail__name">{visitorLabel(visitor)}</h3>
            {visitor.deletedAt && (
              <p className="sc-rail__empty" style={{ marginTop: 6, fontSize: 11.5 }}>
                This visitor requested deletion of their data on {new Date(visitor.deletedAt).toLocaleDateString()}.
              </p>
            )}
          </div>

          {!visitor.deletedAt && (
            <div className="sc-rail__section">
              <span className="sc-rail__label">Contact</span>
              <dl>
                <div className="sc-rail__row"><dt>Name</dt><dd>{visitor.name || <span className="sc-rail__empty">—</span>}</dd></div>
                <div className="sc-rail__row"><dt>Email</dt><dd>{visitor.email || <span className="sc-rail__empty">—</span>}</dd></div>
                <div className="sc-rail__row"><dt>Phone</dt><dd>{visitor.phone || <span className="sc-rail__empty">—</span>}</dd></div>
              </dl>
            </div>
          )}

          {!visitor.deletedAt && visitor.customer && (
            <div className="sc-rail__section">
              <span className="sc-rail__label">Linked customer</span>
              <Link to={`/customers/${visitor.customer.id}`} className="sc-rail__customer-link">
                {`${visitor.customer.firstName || ''} ${visitor.customer.lastName || ''}`.trim() || visitor.customer.email}
              </Link>
            </div>
          )}

          {data.attribution?.orders > 0 && (
            <div className="sc-rail__section sc-rail__section--accent">
              <span className="sc-rail__label">Attributed sales</span>
              <p className="sc-rail__revenue">
                {(data.messages?.[0]?.currency || 'NGN')} {Number(data.attribution.revenue).toLocaleString()}
              </p>
              <p className="sc-rail__revenue-meta">
                {data.attribution.orders} order{data.attribution.orders === 1 ? '' : 's'} placed within 14 days of chatting.
              </p>
            </div>
          )}

          {!visitor.deletedAt && (
            <div className="sc-rail__section">
              <span className="sc-rail__label">Tags</span>
              <TagEditor conversationId={conversationId} visitor={visitor} onChanged={load} />
            </div>
          )}

          {!visitor.deletedAt && (
            <div className="sc-rail__section">
              <span className="sc-rail__label">Where</span>
              <dl>
                <div className="sc-rail__row">
                  <dt><Globe2 size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Location</dt>
                  <dd>{[visitor.city, visitor.country].filter(Boolean).join(', ') || <span className="sc-rail__empty">unknown</span>}</dd>
                </div>
                <div className="sc-rail__row">
                  <dt><DeviceIcon size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Device</dt>
                  <dd>{[visitor.device, visitor.os, visitor.browser].filter(Boolean).join(' · ') || <span className="sc-rail__empty">unknown</span>}</dd>
                </div>
                <div className="sc-rail__row"><dt>Language</dt><dd>{visitor.language || <span className="sc-rail__empty">—</span>}</dd></div>
              </dl>
            </div>
          )}

          {!visitor.deletedAt && (
            <div className="sc-rail__section">
              <span className="sc-rail__label">Activity</span>
              <dl>
                <div className="sc-rail__row"><dt>Landing page</dt><dd style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{visitor.landingPage || <span className="sc-rail__empty">—</span>}</dd></div>
                <div className="sc-rail__row"><dt>Last page</dt><dd style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{visitor.lastPagePath || <span className="sc-rail__empty">—</span>}</dd></div>
                <div className="sc-rail__row"><dt>Pages viewed</dt><dd>{visitor.pagesViewed ?? '—'}</dd></div>
                <div className="sc-rail__row"><dt>Referrer</dt><dd style={{ fontSize: 11 }}>{visitor.referrer || <span className="sc-rail__empty">direct</span>}</dd></div>
              </dl>
            </div>
          )}

          {(visitor.utmSource || visitor.utmMedium || visitor.utmCampaign) && (
            <div className="sc-rail__section">
              <span className="sc-rail__label">UTM</span>
              <dl>
                {visitor.utmSource && <div className="sc-rail__row"><dt>Source</dt><dd>{visitor.utmSource}</dd></div>}
                {visitor.utmMedium && <div className="sc-rail__row"><dt>Medium</dt><dd>{visitor.utmMedium}</dd></div>}
                {visitor.utmCampaign && <div className="sc-rail__row"><dt>Campaign</dt><dd>{visitor.utmCampaign}</dd></div>}
              </dl>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

// ---------- Page shell -----------------------------------------------------

const StorefrontChat = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();

  const [activeTab, setActiveTab] = useState('active');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [socketTick, setSocketTick] = useState(0); // bumps on every socket event
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [chatbotEnabled, setChatbotEnabled] = useState(null); // null | false | true

  // One-shot fetch of the per-store toggle so we can prompt to enable if off
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/merchant/chatbot/settings');
        if (!cancelled) setChatbotEnabled(!!res?.data?.settings?.isEnabled);
      } catch {
        if (!cancelled) setChatbotEnabled(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Single socket connection for this page; events trigger refreshes in both
  // the inbox list and the open conversation by bumping `socketTick`. Auth
  // rides on the `revstar_merchant_token` HTTP-only cookie via
  // withCredentials — there's no localStorage token to read.
  useEffect(() => {
    const sock = socketIo(`${getSocketUrl()}/storefront-chatbot`, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
    socketRef.current = sock;

    const bump = () => setSocketTick((t) => t + 1);
    sock.on('connect', () => setSocketConnected(true));
    sock.on('disconnect', () => setSocketConnected(false));
    sock.on('connect_error', (err) => {
      console.warn('[StorefrontChat] socket connect_error:', err?.message);
    });
    [
      'chatbot:visitor_message',
      'chatbot:ai_reply',
      'chatbot:merchant_reply',
      'chatbot:taken_over',
      'chatbot:released',
      'chatbot:closed',
      'chatbot:escalated',
      'chatbot:new_conversation',
    ].forEach((evt) => sock.on(evt, bump));

    return () => {
      sock.removeAllListeners();
      sock.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Join the open conversation room so we receive its targeted events
  useEffect(() => {
    if (!socketRef.current || !socketConnected || !conversationId) return undefined;
    socketRef.current.emit('conversation:join', { conversationId });
    return () => {
      try { socketRef.current?.emit('conversation:leave', { conversationId }); } catch { /* noop */ }
    };
  }, [conversationId, socketConnected]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const loadInbox = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/merchant/chatbot/conversations', {
        params: { tab: activeTab, q: debouncedQuery || undefined, pageSize: 30 },
      });
      setItems(res?.data?.items || []);
      setTotal(res?.data?.total || 0);
    } catch (err) {
      console.error('[StorefrontChat] inbox load:', err?.response?.data?.error || err?.message);
      // Keep stale items so the UI doesn't blank on transient errors
    } finally {
      setLoading(false);
    }
  }, [activeTab, debouncedQuery]);

  // Reset list loading state when filters change
  useEffect(() => {
    setLoading(true);
    loadInbox();
  }, [activeTab, debouncedQuery, loadInbox]);

  // Re-fetch the list whenever a socket event tells us state changed
  useEffect(() => {
    if (socketTick === 0) return;
    loadInbox();
  }, [socketTick, loadInbox]);

  // Poll inbox as a fallback. When socket is connected we back off to 30s.
  useEffect(() => {
    const interval = socketConnected ? 30000 : INBOX_POLL_MS;
    const t = setInterval(loadInbox, interval);
    return () => clearInterval(t);
  }, [loadInbox, socketConnected]);

  const handleSelect = useCallback((id) => navigate(`/storefront-chat/${id}`), [navigate]);
  const handleBack = useCallback(() => navigate('/storefront-chat'), [navigate]);

  return (
    <Shell>
      <FeatureGate featureId="sales.storefront_chatbot" upgradeMessage="Upgrade to PRO or PREMIUM to enable the storefront AI chatbot.">
        {chatbotEnabled === false && (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
              padding: '12px 18px', margin: '12px 16px 0', borderRadius: 10,
              background: 'linear-gradient(180deg, rgba(13, 116, 99, 0.08), #ffffff)',
              border: '1px solid rgba(13, 116, 99, 0.22)',
              fontSize: 13.5,
            }}
          >
            <span style={{ color: '#0f172a', minWidth: 0 }}>
              <strong style={{ fontWeight: 700 }}>Your chatbot is off.</strong>{' '}
              Visitors won't see the launcher on your storefront until you enable it.
            </span>
            <Link
              to="/storefront-chat/settings"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 14px',
                background: '#0d7463', color: '#ffffff', textDecoration: 'none',
                borderRadius: 8, fontSize: 12.5, fontWeight: 600, letterSpacing: '0.01em',
                flexShrink: 0,
              }}
            >
              Enable now →
            </Link>
          </div>
        )}
        <div className={`sc-page ${conversationId ? 'has-detail' : ''}`}>
          <InboxList
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            query={query}
            setQuery={setQuery}
            items={items}
            total={total}
            loading={loading}
            selectedId={conversationId}
            onSelect={handleSelect}
          />
          {conversationId ? (
            <ConversationView
              conversationId={conversationId}
              onBack={handleBack}
              socketTick={socketTick}
              socketConnected={socketConnected}
            />
          ) : (
            <div className="sc-detail">
              <div className="sc-detail-empty">
                <span className="sc-detail-empty__icon" aria-hidden><MessageSquare size={26} /></span>
                <h3 className="sc-detail-empty__title">Pick a conversation</h3>
                <p className="sc-detail-empty__body">
                  When a visitor chats with your storefront, it will appear in the list. Select one to view the thread and visitor profile.
                </p>
              </div>
            </div>
          )}
        </div>
      </FeatureGate>
    </Shell>
  );
};

export default StorefrontChat;
